import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

// Icon Components - Same as AdminDashboard
const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
  </svg>
);

const RecordIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="3" fill="#000"/>
  </svg>
);

const PersonIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
  </svg>
);

const StarIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const LogoutIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
  </svg>
);

const NewsletterIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const InboxIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
  </svg>
);

const AdminLayout = ({ children, onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && !isSidebarOpen) {
        setIsSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const menuItems = [
    { 
      icon: DashboardIcon, 
      label: 'Dashboard', 
      path: '/admin/dashboard',
      isActive: location.pathname === '/admin/dashboard'
    },
    { 
      icon: RecordIcon, 
      label: 'Episodes', 
      path: '/admin/episodes',
      isActive: location.pathname.startsWith('/admin/episodes')
    },
    { 
      icon: PersonIcon, 
      label: 'Guests', 
      path: '/admin/guests',
      isActive: location.pathname.startsWith('/admin/guests')
    },
    { 
      icon: CalendarIcon, 
      label: 'Upcoming', 
      path: '/admin/upcoming',
      isActive: location.pathname.startsWith('/admin/upcoming')
    },
    { 
      icon: StarIcon, 
      label: 'Sponsors', 
      path: '/admin/sponsors',
      isActive: location.pathname.startsWith('/admin/sponsors')
    },
    { 
      icon: DashboardIcon, // We'll reuse dashboard or generic for Media
      label: 'Media', 
      path: '/admin/media',
      isActive: location.pathname.startsWith('/admin/media')
    },
    { 
      icon: NewsletterIcon, 
      label: 'Newsletter', 
      path: '/admin/newsletter',
      isActive: location.pathname.startsWith('/admin/newsletter')
    },
    { 
      icon: InboxIcon, 
      label: 'Messages', 
      path: '/admin/messages',
      isActive: location.pathname.startsWith('/admin/messages')
    },
    { 
      icon: StarIcon, 
      label: 'Donations', 
      path: '/admin/donations',
      isActive: location.pathname.startsWith('/admin/donations')
    }
  ];

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    onLogout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-midnight text-warm-white">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Fixed Sidebar */}
      <div 
        className={`fixed top-0 left-0 h-full bg-charcoal/95 md:bg-charcoal/40 backdrop-blur-sm border-r-2 border-slate-dark/50 transition-all duration-300 flex flex-col z-50 shadow-2xl
          ${isSidebarOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full md:w-20 md:translate-x-0'}
        `}
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-dark/30">
          <div className="flex items-center justify-between">
            <img 
              src="https://agents.beunframed.com/wp-content/uploads/2025/07/Heading-4.svg" 
              alt="Unframed Talks" 
              className={`h-8 w-auto transition-all duration-300 ${(!isSidebarOpen && window.innerWidth >= 768) && 'hidden'}`}
            />
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-soft-gray hover:text-electric-yellow transition-colors duration-300 p-2 md:block hidden"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="text-soft-gray hover:text-electric-yellow transition-colors duration-300 p-2 md:hidden"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation - Scrollable if needed */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-2">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleNavClick}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                    item.isActive 
                      ? 'bg-electric-yellow text-midnight' 
                      : 'text-soft-gray hover:bg-slate-dark/30 hover:text-electric-yellow'
                  }`}
                >
                  <IconComponent />
                  {(isSidebarOpen || window.innerWidth < 768) && <span className="font-medium">{item.label}</span>}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Fixed Footer */}
        <div className="p-4 border-t border-slate-dark/30 bg-charcoal/40">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-soft-gray hover:bg-red-500/20 hover:text-red-300 rounded-xl transition-all duration-300"
          >
            <LogoutIcon />
            {(isSidebarOpen || window.innerWidth < 768) && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content Area - Offset by sidebar width on desktop */}
      <div className={`transition-all duration-300 min-h-screen flex flex-col ${isSidebarOpen ? 'md:ml-64' : 'md:ml-20'}`}>
        {/* Fixed Top Bar */}
        <header className="sticky top-0 bg-charcoal/20 backdrop-blur-sm border-b border-slate-dark/30 px-4 md:px-8 py-4 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="text-soft-gray hover:text-electric-yellow transition-colors md:hidden"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="text-left hidden md:block">
                <h1 className="font-clash text-xl md:text-2xl font-bold text-electric-yellow text-left">
                  {menuItems.find(item => item.isActive)?.label || 'Admin Panel'}
                </h1>
                <p className="text-soft-gray text-sm mt-1 text-left">
                  Manage your Unframed Talks content
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-soft-gray">
                Welcome back, Admin
              </div>
              <div className="w-8 h-8 bg-electric-yellow rounded-full flex items-center justify-center">
                <span className="text-midnight text-sm font-bold">A</span>
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Page Content */}
        <main className="flex-1 p-4 md:p-8 overflow-y-auto bg-midnight">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;